import React, { useState, useEffect } from 'react';
import { programmesAPI } from '@/lib/api';
import { toast } from 'sonner';
import { DashboardLayout } from '@/components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Clock, 
  Mail, 
  User, 
  Search, 
  Loader2, 
  RefreshCw,
  Download,
  Users
} from 'lucide-react';

const AdminWaitlistPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchWaitlist = async () => {
    setLoading(true);
    try {
      const response = await programmesAPI.getAdminWaitlist();
      setEntries(response.data || []);
    } catch (err) {
      const errorMsg = err.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to load waitlist');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWaitlist();
  }, []);

  const filteredEntries = entries.filter(entry => {
    const search = searchTerm.toLowerCase();
    return (
      entry.name?.toLowerCase().includes(search) ||
      entry.email?.toLowerCase().includes(search) ||
      entry.programme_title?.toLowerCase().includes(search) ||
      entry.programme_slug?.toLowerCase().includes(search)
    );
  });

  // Group by programme
  const groupedByProgramme = filteredEntries.reduce((acc, entry) => {
    const key = entry.programme_slug || 'unknown';
    if (!acc[key]) {
      acc[key] = {
        title: entry.programme_title || entry.programme_slug,
        slug: entry.programme_slug,
        entries: []
      };
    }
    acc[key].entries.push(entry);
    return acc;
  }, {});

  const exportToCSV = () => {
    if (entries.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Name', 'Email', 'Programme', 'Date Joined'];
    const rows = entries.map(e => [
      e.name,
      e.email,
      e.programme_title || e.programme_slug,
      new Date(e.created_at).toLocaleDateString('en-GB')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell || ''}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `waitlist_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Waitlist exported');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#0D1B2A]">Programme Waitlist</h1>
            <p className="text-slate-500 mt-1">
              Users who registered interest in sold-out programmes
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchWaitlist}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportToCSV}
              disabled={entries.length === 0}
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-[#41EAD4]/10 rounded-lg">
                  <Users className="w-6 h-6 text-[#41EAD4]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0D1B2A]">{entries.length}</p>
                  <p className="text-sm text-slate-500">Total Entries</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-[#C9A84C]/10 rounded-lg">
                  <Mail className="w-6 h-6 text-[#C9A84C]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0D1B2A]">
                    {new Set(entries.map(e => e.email)).size}
                  </p>
                  <p className="text-sm text-slate-500">Unique Emails</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-[#0D1B2A]/10 rounded-lg">
                  <Clock className="w-6 h-6 text-[#0D1B2A]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0D1B2A]">
                    {Object.keys(groupedByProgramme).length}
                  </p>
                  <p className="text-sm text-slate-500">Programmes</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name, email, or programme..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#41EAD4]" />
          </div>
        ) : entries.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">No waitlist entries yet</h3>
              <p className="text-slate-500">
                When users join the waitlist for sold-out programmes, they'll appear here.
              </p>
            </CardContent>
          </Card>
        ) : filteredEntries.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">No results found</h3>
              <p className="text-slate-500">Try adjusting your search term.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.values(groupedByProgramme).map((group) => (
              <Card key={group.slug}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold text-[#0D1B2A]">
                      {group.title}
                    </CardTitle>
                    <Badge variant="secondary" className="bg-[#C9A84C]/10 text-[#C9A84C]">
                      {group.entries.length} {group.entries.length === 1 ? 'person' : 'people'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="divide-y">
                    {group.entries.map((entry, idx) => (
                      <div key={entry.waitlist_id || idx} className="py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#0D1B2A] flex items-center justify-center text-white font-semibold">
                            {entry.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-[#0D1B2A]">{entry.name}</p>
                            <a 
                              href={`mailto:${entry.email}`} 
                              className="text-sm text-[#41EAD4] hover:underline flex items-center gap-1"
                            >
                              <Mail className="w-3 h-3" />
                              {entry.email}
                            </a>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-500">
                            {new Date(entry.created_at).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                          <p className="text-xs text-slate-400">
                            {new Date(entry.created_at).toLocaleTimeString('en-GB', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminWaitlistPage;
